import { useMemo, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled/macro';
import { Flex, Tabs, TabList, TabPanels, Tab, TabPanel, useDisclosure } from '@chakra-ui/react';

import { getValidRowsWithDates } from '../../services/grid';
import {
    createForwardCurveData,
    getForwardCurveData,
    updateForwardCurveData,
    deleteForwardCurveData,
    deleteAllForwardCurveData,
    formatParsedNewForwardCurveData,
    createForwardCurveOptions,
    getForwardCurveOptions,
    updateForwardCurveOptions,
    deleteForwardCurveOptions,
    deleteAllForwardCurveOptions,
    formatParsedNewForwardCurveOptions,
} from '../../services/forwardCurve';

import { hasAppliedCustomFilters, getDataFilters } from '../../services/items';

import ItemFilters from '../utils/ItemFilters';
import SideDrawer from '../utils/SideDrawer';
import SecondaryButton from '../utils/SecondaryButton';

import SubItemData from '../itemData/SubItemData';

import { ReactComponent as RefreshIcon } from '../../icons/refresh.svg';
import { ReactComponent as FilterIcon } from '../../icons/filter.svg';

const ForwardCurveData = ({ forwardCurveId, options }) => {
    const intl = useIntl();

    const item = 'forwardCurve';

    const filterDrawer = useDisclosure();

    const [key, setKey] = useState(0);
    const [filters, setFilters] = useState(() => getDataFilters(item));

    const [loadingQueue, setLoadingQueue] = useState([]); // unique keys represeting which resource is in loading state

    const refreshTabs = useCallback(() => setKey((prev) => prev + 1), []);

    const onLoadingStart = useCallback((key) => {
        setLoadingQueue((prev) => prev.concat(key));
    }, []);

    const onLoadingEnd = useCallback((key) => {
        setLoadingQueue((prev) => prev.filter((p) => p !== key));
    }, []);

    const onApplyFilters = (newFilters) => {
        filterDrawer.onClose();

        setFilters(newFilters);
    };

    const forwardCurveDataGridResource = useMemo(() => {
        return {
            create: (rows) => createForwardCurveData(forwardCurveId, rows),
            read: (pagination) => getForwardCurveData(forwardCurveId, { ...pagination, ...filters }),
            update: (rows) => {
                const validRows = getValidRowsWithDates(rows, ['transactionDate', 'deliveryDate', 'updateDateTime']);

                return updateForwardCurveData(forwardCurveId, validRows);
            },
            delete: (rows) =>
                deleteForwardCurveData(
                    forwardCurveId,
                    rows.map((row) => row.loadDataId)
                ),
            deleteAll: () => deleteAllForwardCurveData(forwardCurveId),
        };
    }, [forwardCurveId, filters]);

    const forwardCurveDataColumnSchema = useMemo(() => {
        return {
            transactionDate: { name: 'transactionDate', type: 'date' },
            peakPeriodLabelId: { name: 'peakPeriodLabelId', type: 'string' },
            deliveryDate: { name: 'deliveryDate', type: 'date' },
            price: { name: 'price', type: 'number' },
            updateDateTime: { name: 'updateDateTime', type: 'date' },
        };
    }, []);

    const forwardCurveOptionsGridResource = useMemo(() => {
        return {
            create: (rows) => createForwardCurveOptions(forwardCurveId, rows),
            read: (pagination) => getForwardCurveOptions(forwardCurveId, { ...pagination, ...filters }),
            update: (rows) => {
                const validRows = getValidRowsWithDates(rows, [
                    'transactionDate',
                    'deliveryDate',
                    'updateDateTime',
                    'expiration',
                ]);

                return updateForwardCurveOptions(forwardCurveId, validRows);
            },
            delete: (rows) =>
                deleteForwardCurveOptions(
                    forwardCurveId,
                    rows.map((row) => row.loadDataId)
                ),
            deleteAll: () => deleteAllForwardCurveOptions(forwardCurveId),
        };
    }, [forwardCurveId, filters]);

    const forwardCurveOptionsColumnSchema = useMemo(() => {
        return {
            intervalName: { name: 'intervalName', type: 'string' },
            optionType: { name: 'optionType', type: 'string' },
            price: { name: 'price', type: 'number' },
            strikePrice: { name: 'strikePrice', type: 'number' },
            deliveryDate: { name: 'deliveryDate', type: 'date' },
            peakPeriodId: { name: 'peakPeriodId', type: 'string' },
            bid: { name: 'bid', type: 'number' },
            ask: { name: 'ask', type: 'number' },
            settle: { name: 'settle', type: 'number' },
            vol: { name: 'vol', type: 'number' },
            expiration: { name: 'expiration', type: 'date' },
            transactionDate: { name: 'transactionDate', type: 'date' },
            updateDateTime: { name: 'updateDateTime', type: 'date' },
        };
    }, []);

    const formatNewForwardCurveData = useCallback(
        (data) => formatParsedNewForwardCurveData(data, options.peakPeriods),
        [options.peakPeriods]
    );
    const formatNewForwardCurveOptions = useCallback(
        (data) => formatParsedNewForwardCurveOptions(data, options.peakPeriods),
        [options.peakPeriods]
    );
    return (
        <>
            <Tabs px={0} mt={6} isLazy isManual lazyBehavior="keepMounted" key={key}>
                <StyledTabList w="100%" flexWrap={{ base: 'wrap', xl: 'nowrap' }} alignItems="center">
                    <StyledTab>
                        <FormattedMessage id="forward_curve_tab_data" />
                    </StyledTab>
                    <StyledTab>
                        <FormattedMessage id="forward_curve_tab_model" />
                    </StyledTab>
                    <StyledTab>
                        <FormattedMessage id="forward_curve_tab_options" />
                    </StyledTab>

                    <Flex ml="auto" gridGap={2} order={{ base: -1, xl: 0 }} w={{ base: '100%', xl: 'auto' }}>
                        <SecondaryButton
                            onClick={refreshTabs}
                            size="sm"
                            variant="secondary"
                            leftIcon={<RefreshIcon />}
                            isLoading={loadingQueue.length > 0}
                            loadingText={intl.formatMessage({ id: 'common_loading' })}
                            textTransform="capitalize"
                            ml="auto"
                        >
                            <FormattedMessage id="common_refresh" />
                        </SecondaryButton>
                        <SecondaryButton
                            leftIcon={<FilterIcon />}
                            rightIcon={hasAppliedCustomFilters(filters) && <CustomFilterIndicatorIcon />}
                            type="button"
                            variant="secondary"
                            onClick={filterDrawer.onOpen}
                            size="sm"
                        >
                            <FormattedMessage id="common_filters_btn" />
                        </SecondaryButton>
                    </Flex>
                </StyledTabList>

                <TabPanels mt={4}>
                    <TabPanel>
                        <SubItemData
                            tab="0"
                            onLoadingStart={onLoadingStart}
                            onLoadingEnd={onLoadingEnd}
                            gridResource={forwardCurveDataGridResource}
                            columns={[
                                {
                                    field: 'transactionDate',
                                    type: 'date',
                                    headerName: intl.formatMessage({ id: 'forward_curve_transaction_date' }),
                                    resizable: true,
                                    sortable: true,
                                    sort: 'asc',
                                },
                                {
                                    field: 'peakPeriodLabelId',
                                    type: 'select',
                                    cellEditorParams: { options: options.peakPeriods },
                                    headerName: intl.formatMessage({ id: 'forward_curve_peak_period' }),
                                    resizable: true,
                                    minWidth: 180,
                                },
                                {
                                    field: 'deliveryDate',
                                    type: 'date',
                                    headerName: intl.formatMessage({ id: 'forward_curve_delivery_date' }),
                                    resizable: true,
                                    sortable: true,
                                },
                                {
                                    field: 'price',
                                    type: 'number',
                                    headerName: intl.formatMessage({ id: 'forward_curve_price' }),
                                    resizable: true,
                                    sortable: true,
                                },
                                {
                                    field: 'updateDateTime',
                                    type: 'date',
                                    headerName: intl.formatMessage({ id: 'forward_curve_update_date' }),
                                    resizable: true,
                                    sortable: true,
                                },
                            ]}
                            addRowsValidationSchema={forwardCurveDataColumnSchema}
                            formatNewRows={formatNewForwardCurveData}
                        />
                    </TabPanel>
                    <TabPanel></TabPanel>
                    <TabPanel>
                        <SubItemData
                            tab="0"
                            onLoadingStart={onLoadingStart}
                            onLoadingEnd={onLoadingEnd}
                            gridResource={forwardCurveOptionsGridResource}
                            columns={[
                                {
                                    field: 'intervalName',
                                    headerName: intl.formatMessage({ id: 'forward_curve_interval_type' }),
                                    resizable: true,
                                    sortable: true,
                                },
                                {
                                    field: 'optionType',
                                    headerName: intl.formatMessage({ id: 'forward_curve_option_type' }),
                                    resizable: true,
                                    sortable: true,
                                },
                                {
                                    field: 'price',
                                    type: 'plainNumber',
                                    headerName: intl.formatMessage({ id: 'forward_curve_price' }),
                                    resizable: true,
                                    sortable: true,
                                },
                                {
                                    field: 'strikePrice',
                                    type: 'plainNumber',
                                    headerName: intl.formatMessage({ id: 'forward_curve_strike_price' }),
                                    resizable: true,
                                    sortable: true,
                                },
                                {
                                    field: 'deliveryDate',
                                    type: 'date',
                                    headerName: intl.formatMessage({ id: 'forward_curve_delivery_date' }),
                                    resizable: true,
                                    sortable: true,
                                },
                                {
                                    field: 'peakPeriodId',
                                    type: 'select',
                                    cellEditorParams: { options: options.peakPeriods },
                                    headerName: intl.formatMessage({ id: 'forward_curve_peak_period' }),
                                    resizable: true,
                                    minWidth: 180,
                                },
                                {
                                    field: 'bid',
                                    type: 'plainNumber',
                                    headerName: intl.formatMessage({ id: 'forward_curve_bid' }),
                                    resizable: true,
                                    sortable: true,
                                },
                                {
                                    field: 'ask',
                                    type: 'plainNumber',
                                    headerName: intl.formatMessage({ id: 'forward_curve_ask' }),
                                    resizable: true,
                                    sortable: true,
                                },
                                {
                                    field: 'settle',
                                    type: 'plainNumber',
                                    headerName: intl.formatMessage({ id: 'forward_curve_settle' }),
                                    resizable: true,
                                    sortable: true,
                                },
                                {
                                    field: 'vol',
                                    type: 'plainNumber',
                                    headerName: intl.formatMessage({ id: 'forward_curve_vol' }),
                                    resizable: true,
                                    sortable: true,
                                },
                                {
                                    field: 'expiration',
                                    type: 'date',
                                    headerName: intl.formatMessage({ id: 'forward_curve_expiration_date' }),
                                    resizable: true,
                                    sortable: true,
                                },
                                {
                                    field: 'transactionDate',
                                    type: 'date',
                                    headerName: intl.formatMessage({ id: 'forward_curve_transaction_date' }),
                                    resizable: true,
                                    sortable: true,
                                    sort: 'asc',
                                },
                                {
                                    field: 'updateDateTime',
                                    type: 'date',
                                    headerName: intl.formatMessage({ id: 'forward_curve_update_date' }),
                                    resizable: true,
                                    sortable: true,
                                },
                            ]}
                            addRowsValidationSchema={forwardCurveOptionsColumnSchema}
                            formatNewRows={formatNewForwardCurveOptions}
                        />
                    </TabPanel>
                </TabPanels>
            </Tabs>

            {filterDrawer.isOpen && (
                <SideDrawer
                    isOpen
                    onClose={filterDrawer.onClose}
                    header={<FormattedMessage id="common_filter_heading" values={{ item: 'Forward Curve' }} />}
                    content={<ItemFilters filters={filters} onApplyFilters={onApplyFilters} item={item} />}
                    placement="right"
                />
            )}
        </>
    );
};

// @todo duplicate styles in SubLoadData/SubRenewableData/SubBatteryData
const StyledTabList = styled(TabList)`
    border-bottom: 1px solid var(--chakra-colors-gray-200);

    > [aria-selected='true'] {
        border-bottom: 4px solid var(--chakra-colors-blue-400);
        color: var(--chakra-colors-black);
    }

    > [role='tab'] {
        color: var(--chakra-colors-black);

        &:hover {
            color: var(--chakra-colors-blue-400);
        }
    }
`;

const StyledTab = styled(Tab)`
    padding: 16px 0;
    margin-right: 24px;
    margin-bottom: 0;
    color: var(--chakra-colors-gray-600);
    text-transform: capitalize;

    :focus {
        border-radius: var(--chakra-radii-base);
    }
`;

const CustomFilterIndicatorIcon = styled.div`
    background: var(--chakra-colors-blue-400);
    height: 6px;
    width: 6px;
    border-radius: var(--chakra-radii-md);
    margin-top: -10px;
`;

export default ForwardCurveData;
